import React, { useCallback, useMemo, useRef, useState } from 'react';
import PageTop from '../../../core/components/PageTop';
import FeedsListTable from './table/FeedsListTable';
import { useFeedsInit } from './pagehooks/useFeedsInit';
import { useSelectRow } from '../../../core/table/hooks/useSelectRow';
import { FeedsDataItem } from './feeds_list_types';
import FeedDetailFlyOut from './components/FeedDetailFlyOut';
import FeedsListSearch from './search/FeedsListSearch';
import feedsRepo from '../data/feedsRepo';
import Panel from '../../../core/components/Panel';
import { useAtomValue } from 'jotai';
import { feedsUserContextAtom } from './feeds_list_atoms';

const PageOpsFeedsList = (): JSX.Element => {
    return (
        <>
            <PageTop
                title="Feeds List"
                icon="sprite-feeds"
            />
            <Content />
        </>
    );
};

const Content = (): JSX.Element => {
    const init = useFeedsInit();
    const tableRef = useRef<HTMLTableElement>(null);

    const { selectedRows, setSelectedRows } = useSelectRow();
    const [showDetailFlyOut, setShowDetailFlyOut] = useState<boolean>(false);
    const [showTableFlyOut, setShowTableFlyOut] = useState<boolean>(false);

    const selectedRowsLength = useMemo(() => Object.keys(selectedRows).length, [selectedRows]);
    const selectedRowValues = useMemo(() => Object.values(selectedRows) as FeedsDataItem[], [selectedRows]);
    const { userType } = useAtomValue(feedsUserContextAtom);

    const handleRowClick = useCallback(
        (row: FeedsDataItem): void => {
            let detailedRow = row;
            feedsRepo
                .getDetail(row.Id, userType === 'CE')
                .then((detail: FeedsDataItem) => {
                    if (detail.Records && detail.Records.length > 0) {
                        detailedRow = { ...row, Records: detail.Records };
                    }
                })
                .catch(error => {
                    console.error('Error fetching feed detail:', error);
                })
                .finally(() => {
                    setSelectedRows([detailedRow], false, true);
                    setShowDetailFlyOut(true);
                    setShowTableFlyOut(false);
                });
        },
        [setSelectedRows, userType],
    );

    const handleCloseFlyOut = useCallback((): void => {
        setShowDetailFlyOut(false);
        setSelectedRows([]);
        /* eslint-disable-next-line */
    }, []);

    const handleCloseFlyoutAndRefreshTable = useCallback(async (): Promise<void> => {
        // Close the flyout
        setShowDetailFlyOut(false);
        setSelectedRows([]);
        /* eslint-disable-next-line */
    }, []);

    // Inside PageOpsFeedsList component, before the return:
    const handleEditSearch = useCallback(() => {
        setShowDetailFlyOut(false);
        setSelectedRows([]);
        /* eslint-disable-next-line */
    }, []);

    // Table flyout mutual exclusion
    const tableFlyoutIsOpen = showTableFlyOut && !showDetailFlyOut;
    const menuIsOpen = useMemo(
        () => showDetailFlyOut && selectedRowsLength > 0,
        [showDetailFlyOut, selectedRowsLength],
    );

    if (!init) {
        return <></>;
    }

    return (
        <>
            <div className="feeds-feature feeds-list">
                <Panel
                    flexDirection="column"
                    noMinHeight
                >
                    <FeedsListSearch onEditSearch={handleEditSearch} />
                    <div>
                        <FeedsListTable
                            onClick={handleRowClick}
                            showTableViewEditor
                            isExternalFlyOutOpen={menuIsOpen}
                            showTableFlyOut={tableFlyoutIsOpen}
                            setShowTableFlyOut={setShowTableFlyOut}
                            gridRef={tableRef}
                        />
                    </div>
                </Panel>
            </div>
            <FeedDetailFlyOut
                open={menuIsOpen}
                onClose={handleCloseFlyOut}
                feedItem={selectedRowValues[0]}
                onCloseFlyoutAndRefreshTable={handleCloseFlyoutAndRefreshTable}
                setSelectedRows={setSelectedRows}
                ignoreClickRef={tableRef}
            />
        </>
    );
};

export default PageOpsFeedsList;
